import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { ListAlt, Speed } from "@mui/icons-material";
import { Tables } from "../../../database.types";
import {
  AutoSaveIndicator,
  AutoSaveStatus,
} from "../../components/AutoSaveIndicator";
import { StandardButton } from "../../components/StandardButton";
import { ClassicBonusEditor } from "./ClassicBonusEditor";
import { ClosestGuessEditor } from "./ClosestGuessEditor";
import { useSupabase } from "../../auth/useSupabase";
import { gameService } from "../../data/gameService";

type BonusRoundRow = Tables<"bonus_rounds">;

interface BonusRoundEditorProps {
  bonusRoundId: string | null;
  gameId: string;
  phase: "bonus" | "overtime";
  disabled: boolean;
}

const selectDarkSx = {
  "& .MuiOutlinedInput-root": {
    color: "white",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.6)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255, 255, 255, 0.8)",
      borderWidth: 2,
    },
  },
  "& .MuiSelect-icon": {
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
    "&.Mui-focused": { color: "white" },
  },
};

const BonusRoundEditor = ({
  bonusRoundId: bonusRoundIdProp,
  gameId,
  phase,
  disabled,
}: BonusRoundEditorProps) => {
  const theme = useTheme();
  const { supabase } = useSupabase();

  const [bonusRound, setBonusRound] = useState<BonusRoundRow | null>(null);
  const [loading, setLoading] = useState(bonusRoundIdProp !== null);
  const [creating, setCreating] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveStatus>("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const markSaving = useCallback(() => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setAutoSaveState("saving");
  }, []);

  const markSaved = useCallback(() => {
    setAutoSaveState("saved");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 2000);
  }, []);

  const markError = useCallback(() => {
    setAutoSaveState("error");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 3000);
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  // Fetch bonus round when ID is provided
  useEffect(() => {
    if (!bonusRoundIdProp) return;

    let cancelled = false;
    const load = async () => {
      try {
        const row = await gameService.getBonusRound(
          supabase,
          bonusRoundIdProp,
        );
        if (!cancelled) setBonusRound(row);
      } catch (err) {
        console.error("Error fetching bonus round:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [supabase, bonusRoundIdProp]);

  const handleCreateOvertime = async () => {
    setCreating(true);
    try {
      const row = await gameService.createBonusRound(supabase, {
        game_id: gameId,
        type: "Classic (List)",
        phase: "overtime",
      });
      setBonusRound(row);
    } catch (err) {
      console.error("Error creating overtime round:", err);
    } finally {
      setCreating(false);
    }
  };

  const handleTypeChange = async (newType: string) => {
    if (!bonusRound) return;
    markSaving();
    try {
      const updated = await gameService.updateBonusRound(
        supabase,
        bonusRound.id,
        { type: newType },
      );
      setBonusRound(updated);
      markSaved();
    } catch {
      markError();
    }
  };

  // Show "Create Overtime Round" button when no bonus round exists for overtime
  if (!bonusRoundIdProp && !bonusRound && phase === "overtime") {
    return (
      <Box py={1} display="flex" justifyContent="center">
        <StandardButton
          variant="outlined"
          color="secondary"
          onClick={handleCreateOvertime}
          disabled={creating || disabled}
        >
          {creating ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : null}
          Create Overtime Round
        </StandardButton>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!bonusRound) return null;

  const headerLabel = phase === "overtime" ? "Overtime" : "Bonus Round";

  return (
    <Box py={1} overflow="auto" display="flex" flex={1} width="100%">
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 720,
          mx: "auto",
          borderRadius: 2,
          boxShadow: 2,
          transition: "box-shadow 0.3s ease",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          backgroundColor: theme.palette.primary.dark,
          "&:hover": {
            boxShadow: 4,
          },
          border: `1px solid ${theme.palette.primary.main}`,
        }}
      >
        <CardHeader
          title={
            <Box
              display="flex"
              width="100%"
              justifyContent="space-between"
              px={2}
              bgcolor={theme.palette.primary.dark}
            >
              <Typography
                variant="h5"
                component="h2"
                fontWeight="bold"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                  color: "white",
                }}
              >
                {bonusRound.type === "Classic (List)" ? (
                  <ListAlt />
                ) : (
                  <Speed />
                )}
                {headerLabel}
              </Typography>

              <Box display="flex" mr="24px" flexShrink={0}>
                <AutoSaveIndicator status={autoSaveState} />
              </Box>
            </Box>
          }
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.primary.dark,
            p: { xs: 1, sm: 2 },
          }}
        />
        <CardContent
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            gap: 3,
          }}
        >
          <FormControl fullWidth sx={selectDarkSx}>
            <InputLabel>Bonus Round Type</InputLabel>
            <Select
              value={bonusRound.type}
              label="Bonus Round Type"
              onChange={(e) => handleTypeChange(e.target.value)}
              disabled={disabled}
              data-testid="bonus-round-type-select"
            >
              <MenuItem value="Classic (List)">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ListAlt color="primary" />
                  <Typography>Classic (List)</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="Closest Guess Battle">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Speed color="secondary" />
                  <Typography>Closest Guess Battle</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {bonusRound.type === "Classic (List)" && (
            <ClassicBonusEditor
              bonusRoundId={bonusRound.id}
              disabled={disabled}
            />
          )}

          {bonusRound.type === "Closest Guess Battle" && (
            <ClosestGuessEditor
              bonusRoundId={bonusRound.id}
              disabled={disabled}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BonusRoundEditor;
